import React from 'react'
import { TableContainer, Table, TableBody, TableCell, TableHead, TableRow, makeStyles, Typography, IconButton, Box } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import { json2csv } from 'json-2-csv';
import filesaver from 'file-saver';
import ExcelIcon from 'assets/images/excel.png';

const useStyles = makeStyles((theme) => ({
    tableContainer: {
        maxHeight: 300,
        overflowY: 'auto',
        scrollbarColor: `${theme.palette.text.secondary} ${theme.palette.background.paper}`,
    },
    cellStyle: {
        backgroundColor: theme.palette.background.paper,
        textAlign: "center",
        padding: theme.spacing(0.5),
        border: `1px solid ${theme.palette.text.secondary}`
    },
    headerColumn: {
        color: theme.palette.text.underline
    },
    dataColumn: {
        color: theme.palette.text.underline
    },
    exportButtonContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(1)
    },
    exportButton: {
        padding: theme.spacing(1),
        '& img': {
            width: 16,
            height: 16
        }
    },
    exportIcon: {
        width: 16,
        height: 16
    },
    exportTitle: {
        paddingTop: theme.spacing(1)
    }
}))

const BasicTable = ({ header, data, isLoading = false, enableExport = false, fileName = 'table_export', value }) => {

    const classes = useStyles();
    const { t } = useTranslation();
    const hasCellMergeColumn = header?.includes('ecomTrackingNum');

    const rowSpans = React.useMemo(() => {
        if (!hasCellMergeColumn || !Array.isArray(data) || data.length === 0) {
            return {};
        }
        
        const rowSpans = {};
        let startIndex = 0;

        while (startIndex < data.length) {
            const currentValue = data[startIndex]?.ecomTrackingNum;
            let span = 1;
            while (startIndex + span < data.length && data[startIndex + span]?.ecomTrackingNum === currentValue) {
                span += 1;
            }
            rowSpans[startIndex] = span;
            startIndex += span;
        }
        return rowSpans;

    }, [data, hasCellMergeColumn]);

    const handleExportCSV = () => {
        try {
            if (!header || !data || data.length === 0) {
                console.error('No data to export');
                return;
            }

            // Create array of translated header names
            const translatedHeaderNames = header.map(col => t(col));

            // Transform data to use translated headers as keys
            const csvData = data.map((row) => {
                const rowData = {};
                header.forEach((col, index) => {
                    const translatedCol = translatedHeaderNames[index];
                    if (col === 'ecomTrackingNum') {
                        rowData[translatedCol] = row[col]?.trim().length ? '=' + '"' + row[col] + '"' : "-";
                    } else {
                        rowData[translatedCol] = row[col] || "-";
                    }
                });
                return rowData;
            });

            // Create CSV with translated header keys
            const csv = json2csv(csvData, {
                keys: translatedHeaderNames,
                excelBOM: true,
                wrapBooleans: true,
                emptyFieldValue: '-'
            });

            // Save file
            const date = moment(new Date()).format('MM-DD-YYYY');
            const finalFileName = `${fileName}_${date}.csv`;
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
            filesaver.saveAs(blob, finalFileName);
        } catch (error) {
            console.error('Error exporting CSV:', error);
        }
    };
    return (
        <>
            {enableExport && data && data.length > 0 && (
                <Box className={classes.exportButtonContainer}>
                    <Typography variant="h5" className={classes.exportTitle}>{`${t('small_parcel_tracking')} - ${value}`}</Typography>
                    <IconButton
                        size="small"
                        className={classes.exportButton}
                        onClick={handleExportCSV}
                        data-testid="export-basic-table-csv"
                        title={t('export')}
                    >
                        <img src={ExcelIcon} alt={t('export')} />
                    </IconButton>
                </Box>
            )}
            <TableContainer data-testid='basicTable' className={classes.tableContainer} >
            <Table stickyHeader size="small">
                <TableHead>
                    <TableRow>
                        {header.map((col, colIdx) => (
                            <TableCell key={colIdx} className={classes.cellStyle}>
                                <Typography variant="h4" className={classes.headerColumn}>{t(col)}</Typography>
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {isLoading
                        ? (
                            <TableRow>
                                <TableCell colSpan={header.length} className={classes.cellStyle}>
                                    <Typography variant="h5" className={classes.dataColumn}>{t('loading')}</Typography>
                                </TableCell>
                            </TableRow>
                        ) : data?.length > 0
                            ? (
                                data.map((row, rowIdx) => (
                                    <TableRow key={rowIdx}>
                                        {header.map((col, colIdx) => {
                                            if (col === 'ecomTrackingNum' && hasCellMergeColumn) {
                                                const rowSpan = rowSpans[rowIdx];

                                                if (!rowSpan) {
                                                    return null;
                                                }

                                                return (
                                                    <TableCell key={colIdx} className={classes.cellStyle} rowSpan={rowSpan}>
                                                        <Typography variant="h5" className={classes.dataColumn}>{row[col]}</Typography>
                                                    </TableCell>
                                                );
                                            }

                                            return (
                                                <TableCell key={colIdx} className={classes.cellStyle}>
                                                    <Typography variant="h5" className={classes.dataColumn}>{row[col]}</Typography>
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={header.length} className={classes.cellStyle}>
                                        <Typography variant="h5" className={classes.dataColumn}>{t('noDataAvailable')}</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                </TableBody>
            </Table>
        </TableContainer>
        </>
    )
}

export default BasicTable